// ════════════════════════════════════════════════════════════════════
// Jornadas BAC — API, caché local y sincronización.
// Cada BAC es una jornada de UNA comunidad (puede durar varios días).
// Las visitas, el barrido PAI y el MRCV quedan dentro de la jornada a
// través de visita.jornada_codigo.
// ════════════════════════════════════════════════════════════════════
import API from "../../axios.jsx";
import { db } from "../../db/localDB";

const BASE = "/bac/jornadas";

export const apiListarJornadas  = (params) => API.get(BASE, { params });
export const apiObtenerJornada  = (codigo) => API.get(`${BASE}/${encodeURIComponent(codigo)}`);
export const apiCrearJornada    = (datos) => API.post(BASE, datos);
export const apiEditarJornada   = (codigo, datos) => API.put(`${BASE}/${encodeURIComponent(codigo)}`, datos);
export const apiIniciarJornada  = (codigo) => API.post(`${BASE}/${encodeURIComponent(codigo)}/iniciar`);
export const apiCerrarJornada   = (codigo, formData, config = {}) =>
  API.post(`${BASE}/${encodeURIComponent(codigo)}/cerrar`, formData, { ...config, headers: { "Content-Type": "multipart/form-data" } });
export const apiReabrirJornada  = (codigo, motivo) => API.post(`${BASE}/${encodeURIComponent(codigo)}/reabrir`, { motivo });
export const apiEliminarJornada = (codigo) => API.delete(`${BASE}/${encodeURIComponent(codigo)}`);
export const apiListarLideres   = () => API.get(`${BASE}/lideres`);
export const apiFirmaJornadas   = () => API.get(`${BASE}/firma`);

// Avisa a todas las pantallas que la lista de jornadas cambió
export const avisarCambioJornadas = () => window.dispatchEvent(new CustomEvent("jornadas-cambiaron"));

// ── Utilidades ───────────────────────────────────────────────────────
const siglas = (texto) => {
  if (!texto) return "X";
  const limpio = texto.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9 ]/g, "").trim();
  return (limpio.split(/\s+/)[0] || "X").substring(0, 3).toUpperCase();
};
const pad = (n) => String(n).padStart(2, "0");

// Fecha de hoy en la hora del dispositivo (YYYY-MM-DD)
export const hoyLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Código único generado en el dispositivo — así la jornada se puede
// abrir sin señal y las visitas quedan ligadas desde el primer momento.
export const generarCodigoJornada = (territorio, comunidad) => {
  const d = new Date();
  const sello = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  const azar = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `JOR-${siglas(territorio)}-${siglas(comunidad)}-${sello}-${azar}`;
};

export const fmtFecha = (f) => {
  if (!f) return "—";
  const s = String(f).slice(0, 10);
  const [a, m, d] = s.split("-");
  return a && m && d ? `${d}/${m}/${a}` : s;
};

// Nombre corto para selects y badges
export const etiquetaJornada = (j) => {
  if (!j) return "";
  const rango = j.fecha_fin && j.fecha_fin !== j.fecha_inicio ? `${fmtFecha(j.fecha_inicio)} – ${fmtFecha(j.fecha_fin)}` : fmtFecha(j.fecha_inicio);
  const est = j._pendiente ? "⏳" : j.estado === "abierta" ? "🟢" : j.estado === "programada" ? "🗓️" : "🔒";
  return `${est} ${rango} · ${j.lider_nombre || "—"}`;
};

// ── Caché por comunidad ──────────────────────────────────────────────
export async function cachearJornadas(territorio, comunidad, jornadas) {
  try {
    await db.bac_jornadas_cache.put({ territorio, comunidad, datos: jornadas, cachedAt: new Date().toISOString() });
  } catch (e) { console.warn("⚠️ No se pudo cachear jornadas:", e.message); }
}

export async function leerJornadasCache(territorio, comunidad) {
  try {
    const fila = await db.bac_jornadas_cache.get([territorio, comunidad]);
    return fila?.datos || [];
  } catch { return []; }
}

export async function leerJornadasPendientes(territorio, comunidad) {
  try {
    const filas = await db.bac_jornadas_pendientes
      .filter((r) => r.territorio === territorio && r.comunidad === comunidad && r.sincronizado !== 1)
      .toArray();
    return filas.map((r) => ({
      ...r, id: undefined, _localId: r.id, _pendiente: true, _error: r.sincronizado === 3 ? r.ultimoError : null,
      estado: "abierta", origen: "campo", resumen: null,
    }));
  } catch { return []; }
}

// Abre una jornada en campo SIN señal: queda en la cola y se usa de una
// vez como jornada activa en el dispositivo.
export async function guardarJornadaOffline(datos) {
  const fila = { ...datos, sincronizado: 0, createdAt: new Date().toISOString() };
  const id = await db.bac_jornadas_pendientes.add(fila);
  return { ...fila, _localId: id, _pendiente: true, estado: "abierta", origen: "campo" };
}

export async function contarJornadasPendientes() {
  try { return await db.bac_jornadas_pendientes.where("sincronizado").equals(0).count(); } catch { return 0; }
}

const motivoLegible = (e) => e?.response?.data?.message || e?.message || "Error desconocido";

// Cambia el código de jornada de las visitas que siguen en la cola
// (y el líder de la BAC pasa a ser el de la jornada que se conserva)
const reasignarVisitasPendientes = async (deCodigo, aCodigo, aLider) => {
  const filas = await db.bac_visitas_pendientes.filter((r) => r.jornada_codigo === deCodigo && r.sincronizado !== 1).toArray();
  for (const f of filas) await db.bac_visitas_pendientes.update(f.id, { jornada_codigo: aCodigo, ...(aLider ? { lider_bac: aLider } : {}) });
  return filas.length;
};

// ── Sincronizar jornadas abiertas sin señal ─────────────────────────
// Se corre ANTES que las visitas. Si mientras tanto otra persona abrió
// una jornada en la misma comunidad (409), las visitas de esta jornada
// local se pasan a la que ya existe en el servidor — así nada queda
// suelto y se respeta "una jornada abierta por comunidad".
export async function sincronizarJornadasPendientes() {
  const pendientes = await db.bac_jornadas_pendientes.filter((r) => r.sincronizado === 0).toArray();
  let subidas = 0;
  for (const r of pendientes) {
    const { id, sincronizado, createdAt, ultimoError, ...datos } = r;
    try {
      await apiCrearJornada(datos);
      await db.bac_jornadas_pendientes.update(id, { sincronizado: 1 });
      subidas++;
    } catch (e) {
      const st = e?.response?.status;
      const existente = e?.response?.data?.jornada;
      if (st === 409 && e?.response?.data?.codigo === "jornada_abierta_existente" && existente?.codigo) {
        const n = await reasignarVisitasPendientes(r.codigo, existente.codigo, existente.lider_nombre);
        await db.bac_jornadas_pendientes.update(id, { sincronizado: 1, fusionadaCon: existente.codigo });
        window.dispatchEvent(new CustomEvent("bac-jornada-fusionada", {
          detail: { local: r.codigo, servidor: existente.codigo, lider: existente.lider_nombre, visitas: n, comunidad: r.comunidad },
        }));
        subidas++;
      } else if ([400, 403, 404, 409, 413, 422].includes(st)) {
        await db.bac_jornadas_pendientes.update(id, { sincronizado: 3, ultimoError: motivoLegible(e) });
      } else {
        console.error("❌ Error sync jornada BAC:", e.message);
      }
    }
  }
  if (subidas) avisarCambioJornadas();
  return subidas;
}

// ── Descarga TODAS las jornadas y las deja en el dispositivo ─────────
// Se llama sola al entrar a la BAC con internet (y al recuperar señal),
// así cualquier comunidad tiene su lista de jornadas disponible sin
// conexión, aunque nunca se haya abierto antes en este dispositivo.
// Para detectar jornadas NUEVAS se recuerda qué códigos (y su estado) ya
// conocía este dispositivo. Es solo para avisar: si se pierde, la próxima
// descarga vuelve a tomar la lista como punto de partida sin avisar nada.
const CLAVE_CONOCIDAS = "bac_jornadas_conocidas";
const leerConocidas = () => {
  try { return JSON.parse(localStorage.getItem(CLAVE_CONOCIDAS) || "null"); } catch { return null; }
};
const guardarConocidas = (m) => {
  try { localStorage.setItem(CLAVE_CONOCIDAS, JSON.stringify(m)); } catch { /* sin almacenamiento: no se avisa */ }
};

let precargando = null;
export function precargarJornadasTodas() {
  if (precargando) return precargando;
  precargando = (async () => {
    try {
      const res = await apiListarJornadas({});
      const porComunidad = new Map();
      for (const j of res.data || []) {
        const k = `${j.territorio}|||${j.comunidad}`;
        if (!porComunidad.has(k)) porComunidad.set(k, []);
        porComunidad.get(k).push(j);
      }
      const ahora = new Date().toISOString();

      // ── ¿Qué hay de nuevo respecto a lo que este dispositivo ya conocía? ──
      const conocidas = leerConocidas(); // { codigo: { t, c, e } } | null la 1.ª vez
      const actuales = {};
      const nuevas = [], iniciadas = [];
      for (const j of res.data || []) {
        actuales[j.codigo] = { t: j.territorio, c: j.comunidad, e: j.estado };
        if (!conocidas) continue; // primera descarga: solo se toma como base
        const antes = conocidas[j.codigo];
        if (!antes && j.estado !== "cerrada") nuevas.push(j);
        else if (antes && antes.e === "programada" && j.estado === "abierta") iniciadas.push(j);
      }

      // Comunidades que antes tenían jornadas y ya no (eliminadas en el
      // servidor): se deja su caché vacío en vez de mostrar datos viejos.
      for (const v of Object.values(conocidas || {})) {
        const k = `${v.t}|||${v.c}`;
        if (!porComunidad.has(k)) porComunidad.set(k, []);
      }

      await db.bac_jornadas_cache.bulkPut([...porComunidad.entries()].map(([k, datos]) => {
        const [territorio, comunidad] = k.split("|||");
        return { territorio, comunidad, datos, cachedAt: ahora };
      }));
      guardarConocidas(actuales);

      avisarCambioJornadas(); // las listas abiertas se refrescan solas
      if (nuevas.length || iniciadas.length) {
        window.dispatchEvent(new CustomEvent("bac-jornadas-novedades", { detail: { nuevas, iniciadas } }));
      }
      return porComunidad.size;
    } catch (e) {
      console.warn("⚠️ No se pudieron precargar las jornadas:", e.message);
      return null;
    } finally {
      precargando = null;
    }
  })();
  return precargando;
}

